import {
    createRouter,
    createWebHistory,
    RouteLocationNormalized,
    RouteLocationNormalizedLoaded,
    Router,
    RouteRecordRaw
} from 'vue-router';

import * as tmi from "@/constants";

// Services
import routeManager from "@/router/route-manager";
import { createAuthGuard } from "@/router/auth-guard";

// Plugins
import pinia from "@/plugins/PiniaPlugin";

// Constants
import {
    courseKey,
    discordKey,
    discordRegistrationKey,
    etfListingKey,
    majorIndexBreadthKey,
    notFoundKey,
    nyseBpiKey,
    portfolioBuilderKey,
    positionSizeCalculatorKey,
    relativeStrengthMatrixKey,
    sectorAlertsKey,
    tradeAlertSearchKey
} from "@/types/products/product-definitions";

// Pinia Stores

// User-scoped storage purge (R7)

// import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
// const logger = createDefaultLogger();
// CachedComponentWrapper stays static on purpose: it is a 13-line passthrough that renders
// `<component :is="route.meta.category" />`, fronting components that are ALREADY async. It backs
// 15 routes, so making it lazy would add a serial round trip to each of them in exchange for
// essentially no bytes.
import CachedComponentWrapper from "@/pages/CachedComponentWrapper.vue";
import { sectorDetailsKey } from "@/plugins/SectorToolsPlugin";

import { useCoursesStore } from "@/stores/Courses";
import { NotFound } from "@tmi/types/api/responses";

export const myArticlesKey: string = 'my-articles';
export const myWebinarsKey: string = 'my-webinars';

// Route components are lazy by default. These seven used to be static imports, which meant every
// anonymous visitor — including one landing on a single article — downloaded the signed-in
// dashboard, the sign-in and sign-up pages, the courses screen, the bonus-report search and the
// webinar search inside the entry chunk before anything could render.
const Authorization = () => import( '@/pages/Authorization.vue' );
const Registration = () => import( '@/pages/Registration.vue' );
const Forbidden403 = () => import( '@/pages/Forbidden403.vue' );
const Home = () => import( '@/pages/Home.vue' );
const BonusReportsSearch = () => import( '@/components/search/BonusReportSearch.vue' );
const Courses = () => import( '@/components/courses/Courses.vue' );
const WebinarsSearch = () => import( '@/components/search/WebinarSearch.vue' );

// --- PRODUCTS
const Products = () => import( '@/pages/Product.vue' );

const AllBPIs = () => import( '@/pages/Tools/AllBPIs.vue' );
const StockReplacementStrategyCalculator = () => import( '@/pages/Tools/StockReplacementStrategyCalculator.vue' );
const Article = () => import( '@/pages/Article.vue' );
const TradeAlert = () => import( '@/pages/TradeAlert.vue' );
const Author = () => import( '@/pages/Author.vue' );

// Components used within the Product Pages
export const SectorsAsync = () => import( '@/components/sectors/SectorsList.vue' );
// export const SrsCalculatorAsync = () => import( '@/components/calculators/StockReplacementStrategyCalculator.vue' );

const CalculationHelp = () => import( '@/pages/CalculationHelp.vue' );

const Login = () => import( '@/components/Login.vue' );
const NewPassword = () => import( '@/pages/ResetPassword/NewPassword.vue' );
const Profile = () => import( '@/pages/Profile.vue' );
const MyProfile = () => import( '@/components/my/MyProfile.vue' );
const ResetPassword = () => import( '@/pages/ResetPassword/index.vue' );
const SimpleContentfulPage = () => import( '@/pages/SimpleContentfulPage.vue' );
const SectorEdge = () => import( '@/pages/SectorEdge.vue' );
const SecurityDetails = () => import( '@/components/securities/SecurityDetails.vue' );
const Webinar = () => import( '@/pages/Webinar.vue' );

const ArticleSearch = () => import( "@/components/search/ArticleSearch.vue" );

const AppToolsPage = () => import( '@/pages/Support/AppTools.vue' );

const routes: RouteRecordRaw[] = [
    // -------- Default landing page & alias "/" --------
    {
        path: tmi.HomePath,
        name: tmi.HomeName,
        alias: tmi.DashboardPath,
        meta: { public: true, indexEligible: true },
        component: Home
    },
    // -------- Unauthenticated/Public free content routes (media/sector-edge/sectoredge) --------
    {
        path: tmi.SectorEdgePath,
        name: tmi.SectorEdgeSlug,
        meta: { public: true, indexEligible: true },
        component: SectorEdge
    },
    // -------- Redirector for /sector-edge => /sectoredge --------
    {
        path: tmi.SectorDashEdgePath,
        redirect: tmi.SectorEdgePath
    },
    // -------- Redirector for /media => /sectoredge --------
    {
        path: tmi.MediaPath,
        redirect: tmi.SectorEdgePath
    },
    // -------- Unauthenticated/Public WTF Email Confirmation bullshit route --------
    {
        path: tmi.TestimonialPath,
        meta: { public: true },
        props: {
            slug: tmi.TestimonialSlug,
        },
        component: SimpleContentfulPage
    },
    // -------- Unauthenticated/Public WTF stand-alone testimonial bullshit route --------
    {
        path: tmi.EmailConfirmationPath,
        meta: { public: true },
        props: {
            slug: tmi.EmailConfirmationSlug,
        },
        component: SimpleContentfulPage
    },
    // -------- Unauthenticated/Public informational routes --------
    {
        path: tmi.AboutUsPath,
        redirect: tmi.HomePath,
    },
    {
        path: tmi.ContactUsPath,
        name: tmi.ContactUsDisplayText,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.ContactUsSlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.PrivacyPolicyPath,
        name: tmi.PrivacyPolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.PrivacyPolicySlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.RefundPolicyPath,
        name: tmi.RefundPolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.RefundPolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.TermsOfUsePolicyPath,
        name: tmi.TermsOfUsePolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.TermsOfUsePolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.SmsTextPolicyPath,
        name: tmi.SmsTextPolicySlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.SmsTextPolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.WhitelistInstructionsPath,
        name: tmi.WhitelistInstructionsSlug,
        meta: { public: true, indexEligible: true },
        props: {
            slug: tmi.WhitelistInstructionsSlug
        },
        component: SimpleContentfulPage
    },
    // -------- Authentication routes --------
    {
        path: tmi.SignInPath,
        meta: { public: true, indexEligible: true },
        component: Authorization,
        children: [
            {
                path: tmi.SignInPath,
                name: tmi.SignInName,
                meta: { public: true, indexEligible: true },
                components: {
                    loginSection: Login
                }
            },
            {
                path: tmi.SignInAsUserPath,
                name: tmi.SignInAsUserName,
                meta: { public: true, indexEligible: true },
                components: {
                    loginSection: Login
                }
            }
        ]
    },
    {
        path: tmi.SignUpPath,
        name: tmi.SignUpName,
        meta: { public: true, indexEligible: true },
        component: Registration,
    },
    {
        path: tmi.ResetPasswordPath,
        name: tmi.ResetPasswordName,
        meta: { public: true },
        component: ResetPassword
    },
    {
        path: tmi.NewPasswordPath,
        name: tmi.NewPasswordName,
        meta: { public: true },
        component: NewPassword
    },
    // -------- Product routing --------
    {
        path: tmi.ProductsPath,
        name: tmi.ProductsName,
        alias: tmi.ProductsAdvancedAliasPath,
        component: Products,
        meta: { public: true, indexEligible: true }
    },
    // -------- Tools routes --------
    {
        path: tmi.NyseBpiPath,
        name: tmi.NyseBpiName,
        meta: {
            public: true,
            category: nyseBpiKey
        },
        component: CachedComponentWrapper
    },
    // -------- Content-based routes --------
    {
        path: tmi.ArticleSearchPath,
        name: tmi.ArticleSearchName,
        meta: { public: true },
        component: ArticleSearch
    },
    {
        path: tmi.BonusReportsSearchPath,
        name: tmi.BonusReportsSearchName,
        meta: {
            public: false,
            permission: tmi.BonusReportsPermissionKey
        },
        component: BonusReportsSearch
    },
    {
        path: tmi.AuthorBioPath,
        name: tmi.AuthorBioName,
        meta: { public: true, indexEligible: true },
        component: Author
    },
    // -------- Alert-based routes --------
    {
        path: tmi.ShadowSpikePath,
        name: tmi.ShadowSpikeName,
        meta: {
            public: false,
            permission: tmi.ShadowScannerPermissionKey,
            shortCode: tmi.ShadowScannerShortCode
        },
        component: () => import( "@/pages/ShadowSpikeScanner.vue" )
    },
    {
        path: tmi.TradeAlertsPath,
        name: tmi.TradeAlertsName,
        alias: tmi.LearningTradeAlertsAliasPath,
        meta: {
            public: false,
            permission: tmi.ProductTradeAlertsPermissionKey,
            category: tradeAlertSearchKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.TradeAlertPath,
        name: tmi.TradeAlertName,
        alias: tmi.LearningTradeAlertAliasPath,
        meta: {
            public: false,
            permission: tmi.ProductTradeAlertsPermissionKey
        },
        component: TradeAlert
    },
    // -------- Article-based routes --------
    {
        path: tmi.ArticlePath,
        name: tmi.ArticleName,
        meta: { public: true, indexEligible: true },
        component: Article
    },
    // -------- Webinar-based routes --------
    {
        path: tmi.WebinarSearchPath,
        name: tmi.WebinarsName,
        meta: { public: true, indexEligible: true },
        component: WebinarsSearch
    },
    { path: tmi.WebinarAllAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarCompletedAliasPath, redirect: { path: tmi.WebinarSearchPath, query: { view: 'past' } } },
    { path: tmi.WebinarMainAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarUpcomingAliasPath, redirect: { path: tmi.WebinarSearchPath, query: { view: 'upcoming' } } },
    {
        path: tmi.WebinarPath,
        name: tmi.WebinarName,
        meta: { public: true, indexEligible: true },
        component: Webinar
    },
    // --------  Lesson-based routes --------
    {
        path: tmi.CoursesPath,
        name: tmi.CoursesName,
        meta: {
            public: false,
            permission: tmi.CoursePermissionsKey
        },
        beforeEnter: () => {
            useCoursesStore( pinia ).initializeAsync().then();
        },
        component: Courses
    },
    {
        path: tmi.CoursePath,
        name: tmi.CourseName,
        meta: {
            public: false,
            permission: tmi.CoursePermissionsKey,
            category: courseKey
        },
        beforeEnter: ( to ) => {
            const coursesStore = useCoursesStore( pinia );
            coursesStore.initializeAsync().then();

            if ( typeof to.params.courseSlug !== 'string' ) {
                // TODO: update the UI to display the lack of authorization
                // reject the navigation
                throw new NotFound();
            }

            coursesStore.setSelectCourseByCourseSlug( `${ to.params.courseSlug }` );
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.ForbiddenPath,
        name: tmi.ForbiddenName,
        meta: { public: true },
        component: Forbidden403
    },
    {
        path: tmi.SectorTreePath,
        name: tmi.SectorTreeSlug,
        meta: { public: false, permission: tmi.SectorsPermissionKey },
        props: {
            slug: tmi.SectorTreeSlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.SectorsPath,
        name: tmi.SectorsName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey
        },
        component: SectorsAsync
    },
    {
        path: tmi.EtfListingPath,
        name: tmi.EtfListingName,
        meta: {
            public: false,
            permission: tmi.EtfListingPermissionsKey,
            category: etfListingKey
        },
        component: CachedComponentWrapper,
    },
    {
        path: tmi.RelativeStrengthMatrixPath,
        name: tmi.RelativeStrengthMatrixName,
        alias: tmi.MatrixHubRelativeStrengthPath,
        meta: {
            public: false,
            permission: tmi.RelativeStrengthMatrixPermissionKey,
            category: relativeStrengthMatrixKey
        },

        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorDetailsPath,
        name: tmi.SectorDetailsName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey,
            category: sectorDetailsKey
        },
        component: SectorsAsync,
    },
    {
        path: tmi.SecurityDetailsPath,
        meta: {
            public: false,
            permission: tmi.StockPermissionsKey
        },
        component: SecurityDetails
    },
    {
        path: tmi.EtfDetailsAliasPath,
        redirect: to => {
            return { path: `${ tmi.SecurityDetailsBasePath }/${ to.params.ticker }` }
        }
    },
    {
        path: tmi.StockDetailsAliasPath,
        redirect: to => {
            return { path: `${ tmi.SecurityDetailsBasePath }/${ to.params.ticker }` }
        }
    },
    /* Private routes */
    {
        path: tmi.ProfilePath,
        meta: { public: false, permission: tmi.ProfilePermissionKey },
        component: Profile,
        children: [
            {
                path: '',
                name: tmi.ProfileName,
                props: { activeTab: tmi.Articles },
                meta: { public: false, permission: tmi.ProfilePermissionKey },
                component: MyProfile
            },
            {
                path: tmi.MyLibraryPath,
                name: tmi.MyLibraryName,
                props: {
                    activeTab: tmi.Articles
                },
                meta: {
                    public: false,
                    permission: tmi.ProfilePermissionKey,
                    category: myArticlesKey
                },
                component: CachedComponentWrapper
            },
            {
                path: tmi.MyWebinarsPath,
                name: tmi.MyWebinarsName,
                props: {
                    activeTab: tmi.Webinars
                },
                meta: {
                    public: false,
                    permission: tmi.ProfilePermissionKey,
                    category: myWebinarsKey
                },
                component: CachedComponentWrapper
            }
        ]
    },
    {
        path: tmi.MajorIndexBreadthPath,
        name: tmi.MajorIndexBreadthName,
        meta: {
            public: false,
            permission: tmi.MajorIndexBreadthPermissionsKey,
            category: majorIndexBreadthKey
        },
        component: CachedComponentWrapper
    },
    {
        path: '/tools/options-calculator/calculation-help',
        name: 'calculation-help',
        meta: { public: true },
        component: CalculationHelp
    },
    {
        path: routeManager.spreadCalculatorPath(),
        name: tmi.SpreadCalculatorKey,
        meta: {
            public: false,
            permission: tmi.SpreadCalculatorsPermissionKey,
            category: tmi.SpreadCalculatorKey
        },
        component: CachedComponentWrapper
    },
    {
        path: '/tools/position-size-calculator',
        name: 'PositionSizeCalculator',
        meta: {
            public: false,
            permission: tmi.PositionSizeCalculatorPermissionKey,
            category: positionSizeCalculatorKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.StockReplacementStrategyCalculatorPath,
        name: tmi.StockReplacementStrategyCalculatorName,
        meta: {
            public: false,
            permission: tmi.StockReplacementStrategyCalculatorPermissionKey
        },
        component: StockReplacementStrategyCalculator
    },
    {
        path: tmi.SectorBullishPercentIndicesPath,
        name: tmi.SectorBullishPercentIndicesName,
        alias: tmi.AllSectorBPIsPath,
        meta: {
            public: false,
            permission: tmi.AllSectorBullishPercentIndexesPermissionKey
        },
        component: AllBPIs
    },
    {
        path: tmi.PortfolioBuilderPath,
        alias: tmi.PortfoliosPath,
        name: tmi.PortfolioBuilderName,
        meta: {
            public: false,
            permission: tmi.PortfolioBuilderPermissionKey,
            category: portfolioBuilderKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorAlertsPath,
        name: tmi.SectorAlertsName,
        alias: tmi.AlertsAliasPath,
        meta: {
            public: false,
            permission: tmi.SectorAlertsPermissionKey,
            category: sectorAlertsKey
        },
        component: CachedComponentWrapper
    },
    // {
    //     path: tmi.TradeOfTheDayPath,
    //     name: tmi.TradeOfTheDayName,
    //     meta: {
    //         public: false,
    //         permission: tmi.StockPermissionsKey,
    //         // permission: tmi.TradeOfTheDayPermissionKey,
    //         category: tradeOfTheDayKey
    //     },
    //     component: CachedComponentWrapper
    // },
    {
        path: tmi.AppToolsPath,
        name: tmi.AppToolsName,
        alias: tmi.AppToolsPath,
        meta: {
            public: true
        },
        component: AppToolsPage
    },
    // -------- Discord OAuth routes --------
    {
        path: tmi.DiscordPath,
        name: tmi.DiscordName,
        meta: {
            public: false,
            permission: tmi.DashboardPermissionsKey,
            category: discordKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.DiscordCallbackPath,
        name: tmi.DiscordCallbackName,
        meta: {
            public: false,
            permission: tmi.DiscordPermissionsKey,
            category: discordRegistrationKey
        },
        component: CachedComponentWrapper
    },
    {
        path: "/:catchAll(.*)",
        name: notFoundKey,
        meta: {
            public: true,
            category: notFoundKey
        },
        component: CachedComponentWrapper
    }
]

const router: Router = createRouter( {
    history: createWebHistory( import.meta.env.BASE_URL ),
    /*
      The private shell (layouts/Private.vue) is a h-dvh flex column whose <main> owns the scroll —
      the document itself never scrolls there. vue-router's scroll handling targets the window, so
      returning { top: 0 } was a no-op on every authenticated route and navigations landed the user
      wherever the previous page had been scrolled to. Reset <main> explicitly, and keep returning
      the position for the public routes, where the document really is the scroller.
    */
    scrollBehavior( _: RouteLocationNormalized, __: RouteLocationNormalizedLoaded, savedPosition ) {
        return savedPosition || new Promise( ( resolve ) => {
            setTimeout( () => {
                const main: HTMLElement | null = document.querySelector( 'main' );
                if ( main && main.scrollTop > 0 ) {
                    main.scrollTo( { top: 0 } );
                }
                resolve( { top: 0 } );
            }, 300 )
        } );
    },
    routes
} );

/*
---------------------------------------------
The Full Navigation Resolution Flow
---------------------------------------------
  * Navigation triggered.
     1. Call beforeRouteLeave guards in deactivated components.
     2. Call beforeRouteUpdate guards in reused components.
     3. Call beforeEnter in route configs.
     4. Resolve async route components.
     5. Call beforeRouteEnter in activated components.
     6. Call global beforeResolve guards.
     7. Navigation is confirmed.
     8. Call global afterEach hooks.
     9. DOM updates triggered.
    10. Call callbacks passed to next in beforeRouteEnter guards with instantiated instances
*/

// Step #6 in the lifecycle flow above. The guard body lives in auth-guard.ts (tmi-6gh, LD6).
router.beforeResolve( createAuthGuard( pinia ) );

export default router;
